import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import RoomRail from './RoomRail'
import type { LineupSlot, SlotCode, UnitBoardEntry } from '../lib/types'
import { isPickable, reasonUnavailable, slotAccepts, slotLabel } from '../lib/rules'

interface Props {
  slots: SlotCode[]
  board: UnitBoardEntry[]
  myPicks: LineupSlot[]
  onPick: (slot: SlotCode, unitId: string) => void
  onClear: (slot: SlotCode) => void
  error: string | null
}

export default function PickBoard({ slots, board, myPicks, onPick, onClear, error }: Props) {
  const [active, setActive] = useState<SlotCode | null>(null)
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<UnitBoardEntry | null>(null)

  // Last season's numbers for all 192 rooms, once. Small enough (192 rows) to
  // fetch whole and index in memory, which keeps the card render synchronous —
  // a per-card query would be 32 round trips every time a slot opens.
  const [hist, setHist] = useState<Record<string, { avg: number; rank: number; total: number; trend: number | null }>>({})
  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from('v_unit_season')
        .select('team,position,avg_points,pos_rank,pos_total,trend_delta')
        .eq('season', 2025)
      const m: Record<string, { avg: number; rank: number; total: number; trend: number | null }> = {}
      for (const r of (data ?? []) as Record<string, number | string>[]) {
        m[`${r.team}|${r.position}`] = {
          avg: Number(r.avg_points), rank: Number(r.pos_rank),
          total: Number(r.pos_total),
          trend: r.trend_delta === null ? null : Number(r.trend_delta),
        }
      }
      setHist(m)
    })()
  }, [])

  const pickBySlot = useMemo(
    () => Object.fromEntries(myPicks.map((p) => [p.slot, p])) as Record<SlotCode, LineupSlot>,
    [myPicks],
  )
  const byUnitId = useMemo(
    () => Object.fromEntries(board.map((b) => [b.unit.id, b])),
    [board],
  )
  const usedThisWeek = new Set(myPicks.map((p) => p.unit_id))

  const candidates = useMemo(() => {
    if (!active) return []
    return board
      .filter((e) => slotAccepts(active, e.unit.position))
      .filter((e) => !usedThisWeek.has(e.unit.id))
      .filter((e) =>
        q ? (e.unit.team + ' ' + (e.team?.nickname ?? '')).toLowerCase().includes(q.toLowerCase()) : true,
      )
      .sort((a, b) => {
        if (isPickable(a) !== isPickable(b)) return isPickable(a) ? -1 : 1
        return (b.seasonAvg ?? 0) - (a.seasonAvg ?? 0)
      })
  }, [active, board, q, usedThisWeek])

  return (
    <div className="pickboard-wrap">
    <div className="pickboard">
      <div className="lineup">
        {slots.map((s) => {
          const pick = pickBySlot[s]
          const entry = pick ? byUnitId[pick.unit_id] : undefined
          return (
            <button
              key={s}
              className={`slot ${active === s ? 'active' : ''} ${pick ? 'filled' : ''}`}
              onClick={() => setActive(active === s ? null : s)}
            >
              <span className="slot-code">{slotLabel(s)}</span>
              {entry ? (
                <span className="slot-unit">
                  <strong>{entry.unit.team}</strong>
                  <small>
                    {entry.onBye ? 'BYE' : `${entry.isHome ? 'vs' : '@'} ${entry.opponent}`}
                    {entry.locked && ' · locked'}
                  </small>
                </span>
              ) : (
                <span className="slot-empty">
                  <span className="slot-plus" aria-hidden>+</span>
                  Pick a room
                </span>
              )}
              {pick && (
                <span
                  className="slot-x"
                  role="button"
                  aria-label={`Clear ${s}`}
                  onClick={(e) => { e.stopPropagation(); onClear(s) }}
                >×</span>
              )}
            </button>
          )
        })}
      </div>

      {error && <p className="error">{error}</p>}

      {!active && (
        <p className="pick-hint">
          Tap a slot above to take a team's whole position room for the week —
          every player in it scores for you. Spend it and it's gone until January.
        </p>
      )}

      {active && (
        <div className="picker">
          <div className="picker-head">
            <h3>
              Pick your {slotLabel(active)}
              <span className="picker-count">{candidates.filter((c) => !reasonUnavailable(c)).length} available</span>
            </h3>
            <input
              placeholder="Filter teams…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="unit-grid">
            {candidates.map((e) => {
              const why = reasonUnavailable(e)
              return (
                <button
                  key={e.unit.id}
                  className={`unit ${why ? 'unavailable' : ''}`}
                  disabled={!!why}
                  style={{ '--team': e.team?.primary_hex ?? '#444' } as React.CSSProperties}
                  onClick={() => { onPick(active, e.unit.id); setActive(null); setQ('') }}
                >
                  <span className="unit-team">{e.unit.team}</span>
                  <span className="unit-pos">{slotLabel(e.unit.position)}</span>
                  <span className="unit-meta">
                    {e.onBye ? 'BYE' : `${e.isHome ? 'vs' : '@'} ${e.opponent}`}
                  </span>
                  {(() => {
                    const h = hist[`${e.unit.team}|${e.unit.position}`]
                    if (!h) return <span className="unit-avg">—</span>
                    return (
                      <span className="unit-stat">
                        <b>{h.avg}</b>
                        <em>#{h.rank}/{h.total}</em>
                        {h.trend !== null && (
                          <i className={h.trend > 0.5 ? 'up' : h.trend < -0.5 ? 'down' : 'flat'}>
                            {h.trend > 0.5 ? '▲' : h.trend < -0.5 ? '▼' : '–'}
                          </i>
                        )}
                      </span>
                    )
                  })()}
                  {why && <span className="unit-why">{why}</span>}
                  <span
                    className="unit-more"
                    role="button"
                    aria-label={`Stats for ${e.unit.team}`}
                    onClick={(ev) => { ev.stopPropagation(); setOpen(e) }}
                  >stats</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

    </div>

      {/* The rail is always mounted, never conditional. With no room selected
          it teaches the game; with one selected it shows that room's numbers.
          Mounting it only on selection would mean a new manager's first sight
          of the product is a wide empty column. */}
      <RoomRail
        team={open?.unit.team ?? null}
        position={open?.unit.position ?? null}
        opponent={open?.opponent ?? null}
        pickable={open ? !reasonUnavailable(open) : false}
        slotsFilled={myPicks.length}
        slotsTotal={slots.length}
        onClose={open ? () => setOpen(null) : undefined}
        onPick={open && active ? () => {
          onPick(active, open.unit.id)
          setOpen(null); setActive(null); setQ('')
        } : undefined}
      />
    </div>
  )
}
